import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useConfig } from "../hooks/useConfig";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { CheckCircle, Upload, X, ArrowRight, Eye, EyeOff, ArrowLeft, Moon, Sun } from "lucide-react";
import API, { formatError } from "../lib/api";

const STEP_LABELS = ["Account", "Profile", "Skills", "Preferences"];

export default function StudentSignup() {
  const { register, user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { config } = useConfig();
  const navigate = useNavigate();

  const BRANCHES = config.branches;
  const YEARS = config.graduation_years;
  const BITS_CAMPUSES = config.colleges;
  const ROLE_TYPES = config.role_types;
  const COMPANY_TYPES = config.company_types;
  const LOCATIONS = config.locations;
  const PREDEFINED_SKILLS = config.skills;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillDone, setPrefillDone] = useState(false);

  // Theme-aware styles
  const pageBg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)';
  const cardShadow = isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.06)';
  const textPrimary = isDark ? '#ffffff' : '#0a0a0f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const textMuted = isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8';
  const inputStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)', color: '#0a0a0f', border: '1px solid rgba(0,0,0,0.1)' };
  const selectStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)', color: '#0a0a0f', border: '1px solid rgba(0,0,0,0.1)' };
  const accent = '#059669';
  const accentDark = '#047857';
  const accentLight = isDark ? 'rgba(5,150,105,0.15)' : 'rgba(5,150,105,0.08)';
  const accentBorder = 'rgba(5,150,105,0.25)';

  useEffect(() => {
    if (user?.role === "student" && step === 1) navigate("/student/dashboard");
  }, [user, navigate, step]);

  useEffect(() => {
    window.history.replaceState(null, "", "/");
    window.history.pushState(null, "", "/student/signup");

    const handlePopState = () => {
      setStep((s) => (s > 1 ? s - 1 : s));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const advanceToStep = (n) => {
    window.history.pushState(null, "", "/student/signup");
    setStep(n);
  };

  // Step 1 state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  // Step 2 state
  const [college, setCollege] = useState("");
  const [branch, setBranch] = useState("");
  const [cgpa, setCgpa] = useState("");
  const [gradYear, setGradYear] = useState(2026);

  // Step 3 state
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [skills, setSkills] = useState([]);
  const [classifiedRole, setClassifiedRole] = useState("");
  const [aiSummary, setAiSummary] = useState("");

  const [skillSearch, setSkillSearch] = useState("");
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const skillDropdownRef = useRef(null);

  // Step 4 state
  const [preferredRoleType, setPreferredRoleType] = useState("");
  const [preferredCompanyTypes, setPreferredCompanyTypes] = useState([]);
  const [preferredLocations, setPreferredLocations] = useState([]);
  const [openToInternships, setOpenToInternships] = useState(true);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(e.target)) {
        setShowSkillDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSkills = PREDEFINED_SKILLS.filter(
    (s) => s.toLowerCase().includes(skillSearch.toLowerCase()) && !skills.includes(s)
  ).slice(0, 8);

  const addSkillFromDropdown = (skill) => {
    if (!skills.includes(skill) && skills.length < 10) {
      setSkills([...skills, skill]);
      setSkillSearch("");
      setShowSkillDropdown(false);
    }
  };

  const removeSkill = (skill) => setSkills(skills.filter((s) => s !== skill));

  const clearResume = (e) => {
    e.stopPropagation();
    setResumeFile(null);
    setParsedData(null);
    setSkills([]);
    setClassifiedRole("");
    setAiSummary("");
    const input = document.getElementById("resume-input");
    if (input) input.value = "";
  };

  const toggleMultiSelect = (value, list, setList) => {
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  const toggleLocation = (loc) => {
    if (loc === "Anywhere in India") {
      setPreferredLocations(prev =>
        prev.includes("Anywhere in India") ? [] : ["Anywhere in India"]
      );
    } else {
      if (preferredLocations.includes("Anywhere in India")) return;
      setPreferredLocations(prev =>
        prev.includes(loc) ? prev.filter(v => v !== loc) : [...prev, loc]
      );
    }
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    advanceToStep(2);
  };

  const handlePrefillUpload = async (file) => {
    if (!file) return;
    setPrefillLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await API.post("/api/parse-resume", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.is_resume === false) {
        setError("This doesn't look like a resume. Please upload your CV in PDF format.");
        return;
      }
      const d = res.data;
      if (d.name) setName(d.name);
      if (d.email) setEmail(d.email);
      if (d.phone) setPhone(d.phone);
      if (d.cgpa) setCgpa(String(d.cgpa));
      if (d.graduation_year && d.graduation_year > 2020) setGradYear(d.graduation_year);
      if (d.extracted_skills?.length) setSkills(d.extracted_skills);
      if (d.classified_role) setClassifiedRole(d.classified_role);
      if (d.ai_summary) setAiSummary(d.ai_summary);
      setResumeFile(file);
      setParsedData(d);
      setPrefillDone(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleResumeUpload = async (file) => {
    if (!file) return;
    setResumeFile(file);
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await API.post("/api/parse-resume", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.is_resume === false && !res.data.ai_error) {
        setError("This doesn't appear to be a resume. Please upload your CV or resume in PDF format.");
        setResumeFile(null);
        const input = document.getElementById("resume-input");
        if (input) input.value = "";
        return;
      }
      setParsedData(res.data);
      setSkills(res.data.extracted_skills || []);
      setClassifiedRole(res.data.classified_role || "");
      setAiSummary(res.data.ai_summary || "");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    setError("");
    try {
      if (!accountCreated) {
        await register(email, password, name);
        setAccountCreated(true);
      }

      await API.post("/api/student/profile", {
        name,
        college,
        branch,
        cgpa: parseFloat(cgpa) || 0,
        graduation_year: gradYear,
        phone: phone || null,
        classified_role: classifiedRole,
        extracted_skills: skills,
        ai_summary: aiSummary,
        preferred_role_type: preferredRoleType,
        preferred_company_types: preferredCompanyTypes,
        preferred_locations: preferredLocations,
        open_to_ppo: true,
      });

      if (resumeFile) {
        try {
          const formData = new FormData();
          formData.append("file", resumeFile);
          await API.post("/api/student/save-resume", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (resumeErr) {
          console.warn("Resume storage failed (non-fatal):", resumeErr);
        }
      }

      navigate("/student/dashboard");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const optionBg = isDark ? '#0d0d14' : '#ffffff';
  const dropdownBg = isDark ? '#0d0d14' : '#ffffff';
  const dropdownBorder = isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative" style={{ background: pageBg }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${isDark ? 'rgba(5,150,105,0.1)' : 'rgba(5,150,105,0.05)'}, transparent)` }} />
      </div>

      {/* Theme toggle */}
      <button onClick={e => toggleTheme(e)} aria-label="Toggle theme"
        style={{ position: 'absolute', top: '16px', right: '16px', width: '34px', height: '34px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', zIndex: 10 }}>
        {isDark ? <Sun size={14} /> : <Moon size={14} />}
      </button>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4" style={{ textDecoration: 'none' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})` }}>
              <span className="text-white font-bold text-sm">W</span>
            </div>
            <span className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>WonStepCareer</span>
          </Link>

          {/* Step progress */}
          <div className="flex items-center justify-center gap-1 mt-4">
            {STEP_LABELS.map((label, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className={`flex items-center gap-1.5 ${step > i + 1 ? "" : step === i + 1 ? "" : "opacity-40"}`}>
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2"
                    style={step > i + 1
                      ? { background: `linear-gradient(135deg, ${accent}, ${accentDark})`, borderColor: accent, color: 'white' }
                      : step === i + 1
                        ? { borderColor: accent, color: accent, background: 'transparent' }
                        : { borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', color: textMuted, background: 'transparent' }
                    }
                  >
                    {step > i + 1 ? <CheckCircle size={12} /> : i + 1}
                  </div>
                  <span
                    className="text-xs font-medium hidden sm:block"
                    style={{ color: step > i + 1 ? accent : step === i + 1 ? textPrimary : textMuted }}
                  >
                    {label}
                  </span>
                </div>
                {i < 3 && (
                  <div className="w-6 h-0.5" style={{ background: step > i + 1 ? accent : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
                )}
              </div>
            ))}
          </div>
        </div>

        <Card key={step} className="shadow-2xl page-transition" style={{ background: cardBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: cardBorder, boxShadow: cardShadow }}>

          {/* ── Step 1: Account ── */}
          {step === 1 && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Create your account</CardTitle>
                <CardDescription style={{ color: textSecondary }}>Start your skill-based placement journey</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Resume pre-fill */}
                {!prefillDone ? (
                  <div
                    className="mb-4 rounded-xl p-4 text-center cursor-pointer transition-colors"
                    onClick={() => !prefillLoading && document.getElementById("prefill-resume-input").click()}
                    style={{ border: prefillLoading ? `2px dashed ${accent}` : isDark ? '2px dashed rgba(255,255,255,0.12)' : '2px dashed rgba(0,0,0,0.1)', background: prefillLoading ? accentLight : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                    data-testid="prefill-upload-zone"
                  >
                    <input id="prefill-resume-input" type="file" accept=".pdf" className="hidden" onChange={e => handlePrefillUpload(e.target.files[0])} />
                    {prefillLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
                        <span className="text-sm" style={{ color: accent }}>Reading your resume...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <Upload size={18} style={{ color: textMuted }} />
                        <p className="text-sm font-medium" style={{ color: textSecondary }}>Upload resume to auto-fill form</p>
                        <p className="text-xs" style={{ color: textMuted }}>Optional · PDF · AI extracts your name, email, CGPA & more</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-4 p-3 rounded-xl flex items-start gap-3" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
                    <CheckCircle size={16} style={{ color: accent, flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <p className="text-sm font-medium" style={{ color: accent }}>Resume auto-filled</p>
                      <p className="text-xs" style={{ color: '#047857' }}>Review the fields below and correct anything the AI got wrong.</p>
                    </div>
                    <button type="button" onClick={() => { setPrefillDone(false); setResumeFile(null); setParsedData(null); setName(""); setEmail(""); setPhone(""); setCgpa(""); setSkills([]); setClassifiedRole(""); setAiSummary(""); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: textMuted, flexShrink: 0 }}>
                      <X size={14} />
                    </button>
                  </div>
                )}

                <form onSubmit={handleStep1} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-sm" style={{ color: textSecondary }}>Full Name</Label>
                    <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your full name" required className="mt-1 placeholder:text-gray-400" style={inputStyle} data-testid="signup-name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm" style={{ color: textSecondary }}>Email</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@bits-pilani.ac.in" required className="mt-1 placeholder:text-gray-400" style={inputStyle} data-testid="signup-email" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm" style={{ color: textSecondary }}>Mobile Number</Label>
                    <Input id="phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" className="mt-1 placeholder:text-gray-400" style={inputStyle} data-testid="signup-phone" />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-sm" style={{ color: textSecondary }}>Password</Label>
                    <div className="relative mt-1">
                      <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" required className="pr-10 placeholder:text-gray-400" style={inputStyle} data-testid="signup-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: textMuted }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword" className="text-sm" style={{ color: textSecondary }}>Confirm Password</Label>
                    <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" required className="mt-1 placeholder:text-gray-400" style={inputStyle} data-testid="signup-confirm-password" />
                  </div>
                  {error && <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="signup-error">{error}</p>}
                  <Button type="submit" className="w-full font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="signup-submit-btn">
                    Continue <ArrowRight size={16} className="ml-2" />
                  </Button>
                  <p className="text-center text-sm" style={{ color: textSecondary }}>
                    Already have an account?{" "}
                    <Link to="/student/login" className="font-medium hover:underline" style={{ color: accent }}>Sign in</Link>
                  </p>
                </form>
              </CardContent>
            </>
          )}

          {/* ── Step 2: Profile ── */}
          {step === 2 && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Your academic profile</CardTitle>
                <CardDescription style={{ color: textSecondary }}>Tell companies about your background</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); advanceToStep(3); }} className="space-y-4">
                  <div>
                    <Label className="text-sm" style={{ color: textSecondary }}>Select your BITS Campus</Label>
                    <select value={college} onChange={e => setCollege(e.target.value)} required className="mt-1 w-full px-3 py-2 rounded-md text-sm focus:outline-none" style={selectStyle} data-testid="signup-college">
                      <option value="" style={{ background: optionBg }}>Select campus...</option>
                      {BITS_CAMPUSES.map(c => <option key={c} value={c} style={{ background: optionBg }}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm" style={{ color: textSecondary }}>Branch / Stream</Label>
                    <select value={branch} onChange={e => setBranch(e.target.value)} required className="mt-1 w-full px-3 py-2 rounded-md text-sm focus:outline-none" style={selectStyle} data-testid="signup-branch">
                      <option value="" style={{ background: optionBg }}>Select your branch</option>
                      {BRANCHES.map(b => <option key={b} style={{ background: optionBg }}>{b}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm" style={{ color: textSecondary }}>CGPA (out of 10)</Label>
                      <Input type="number" step="0.1" min="0" max="10" value={cgpa} onChange={e => setCgpa(e.target.value)} placeholder="8.5" required className="mt-1 placeholder:text-gray-400" style={inputStyle} data-testid="signup-cgpa" />
                    </div>
                    <div>
                      <Label className="text-sm" style={{ color: textSecondary }}>Graduation Year</Label>
                      <select value={gradYear} onChange={e => setGradYear(Number(e.target.value))} className="mt-1 w-full px-3 py-2 rounded-md text-sm focus:outline-none" style={selectStyle} data-testid="signup-grad-year">
                        {YEARS.map(y => <option key={y} style={{ background: optionBg }}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                  {error && <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>{error}</p>}
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => window.history.back()} className="flex-1" style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }}>Back</Button>
                    <Button type="submit" className="flex-1 font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="profile-continue-btn">
                      Continue <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {/* ── Step 3: Skills & Resume ── */}
          {step === 3 && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Skills & Resume</CardTitle>
                <CardDescription style={{ color: textSecondary }}>Upload your resume for AI skill extraction, or add skills manually</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className="rounded-xl p-6 text-center cursor-pointer transition-colors"
                  onClick={() => !uploading && document.getElementById("resume-input").click()}
                  style={{
                    border: uploading ? `2px dashed ${accent}` : isDark ? '2px dashed rgba(255,255,255,0.15)' : '2px dashed rgba(0,0,0,0.1)',
                    background: uploading ? accentLight : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
                  }}
                  data-testid="resume-upload-zone"
                >
                  <input id="resume-input" type="file" accept=".pdf" className="hidden" onChange={e => handleResumeUpload(e.target.files[0])} />
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
                      <p className="text-sm" style={{ color: accent }}>AI is parsing your resume...</p>
                    </div>
                  ) : resumeFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle size={24} className="text-green-500" />
                      <p className="text-sm font-medium text-green-500">{resumeFile.name}</p>
                      <p className="text-xs" style={{ color: textMuted }}>Resume uploaded successfully</p>
                      <button type="button" onClick={clearResume} className="flex items-center gap-1 text-xs hover:text-red-500 transition-colors mt-1" style={{ color: textMuted }} data-testid="remove-resume-btn">
                        <X size={12} /> Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={24} style={{ color: textMuted }} />
                      <p className="text-sm font-medium" style={{ color: textSecondary }}>Upload Resume (PDF)</p>
                      <p className="text-xs" style={{ color: textMuted }}>Optional — max 5MB. AI will extract your skills.</p>
                    </div>
                  )}
                </div>

                {parsedData && (
                  parsedData.ai_error ? (
                    <div className="p-3 rounded-lg" style={{ background: '#FEF9C3', border: '1px solid #EAB308' }}>
                      <p className="text-xs font-medium" style={{ color: '#854D0E' }}>AI extraction unavailable — please enter skills manually</p>
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
                      <p className="text-xs font-medium" style={{ color: '#059669' }}>
                        AI detected role: <span className="font-bold">{parsedData.classified_role || "Not detected — please enter below"}</span>
                      </p>
                      {parsedData.ai_summary && <p className="text-xs mt-1" style={{ color: '#047857' }}>{parsedData.ai_summary}</p>}
                    </div>
                  )
                )}

                <div>
                  <Label className="text-sm" style={{ color: textSecondary }}>Skills <span className="font-normal" style={{ color: textMuted }}>(up to 10)</span></Label>
                  <div ref={skillDropdownRef} className="relative mt-1">
                    <Input
                      value={skillSearch}
                      onChange={e => { setSkillSearch(e.target.value); setShowSkillDropdown(true); }}
                      onFocus={() => setShowSkillDropdown(true)}
                      placeholder="Search and select skills..."
                      className="placeholder:text-gray-400"
                      style={inputStyle}
                      data-testid="skill-input"
                    />
                    {showSkillDropdown && filteredSkills.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 rounded-lg shadow-2xl max-h-48 overflow-auto" style={{ background: dropdownBg, border: dropdownBorder }}>
                        {filteredSkills.map(skill => (
                          <button
                            key={skill}
                            type="button"
                            onMouseDown={e => { e.preventDefault(); addSkillFromDropdown(skill); }}
                            className="w-full text-left px-3 py-2 text-sm transition-colors"
                            style={{ color: textSecondary }}
                          >
                            {skill}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {skills.map(s => (
                        <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border" style={{ background: isDark ? 'rgba(5,150,105,0.15)' : accent, borderColor: accentBorder, color: isDark ? '#34d399' : '#ffffff' }} data-testid={`skill-badge-${s}`}>
                          {s}
                          <button onClick={() => removeSkill(s)} className="ml-1 hover:text-red-500"><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {error && <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="step3-error">{error}</p>}

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => window.history.back()} className="flex-1" style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }}>Back</Button>
                  <Button type="button" onClick={() => advanceToStep(4)} className="flex-1 font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="skills-continue-btn">
                    Continue <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* ── Step 4: Preferences ── */}
          {step === 4 && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Your Preferences</CardTitle>
                <CardDescription style={{ color: textSecondary }}>Help companies understand what you're looking for</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label className="text-sm" style={{ color: textSecondary }}>Preferred Role Type</Label>
                  <select value={preferredRoleType} onChange={e => setPreferredRoleType(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md text-sm focus:outline-none" style={selectStyle} data-testid="pref-role-type">
                    <option value="" style={{ background: optionBg }}>Select a role type...</option>
                    {ROLE_TYPES.map(r => <option key={r} value={r} style={{ background: optionBg }}>{r}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-sm block mb-2" style={{ color: textSecondary }}>
                    Preferred Company Type <span className="font-normal" style={{ color: textMuted }}>(select all that apply)</span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {COMPANY_TYPES.map(ct => (
                      <button
                        key={ct}
                        type="button"
                        onClick={() => toggleMultiSelect(ct, preferredCompanyTypes, setPreferredCompanyTypes)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                        style={preferredCompanyTypes.includes(ct)
                          ? { background: `linear-gradient(135deg, ${accent}, ${accentDark})`, borderColor: accent, color: 'white' }
                          : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', color: textSecondary }
                        }
                        data-testid={`company-type-${ct}`}
                      >
                        {ct}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm block mb-2" style={{ color: textSecondary }}>
                    Preferred Work Location <span className="font-normal" style={{ color: textMuted }}>(select all that apply)</span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {LOCATIONS.map(loc => {
                      const isAnywhereSelected = preferredLocations.includes("Anywhere in India");
                      const isSelected = preferredLocations.includes(loc);
                      const isDisabled = loc !== "Anywhere in India" && isAnywhereSelected;
                      return (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => toggleLocation(loc)}
                          disabled={isDisabled}
                          className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                          style={isSelected
                            ? { background: accentLight, borderColor: accentBorder, color: accent }
                            : isDisabled
                              ? { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: textMuted, cursor: 'not-allowed' }
                              : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', color: textSecondary }
                          }
                          data-testid={`location-${loc}`}
                        >
                          {loc}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="max-w-[220px]">
                  <div className="p-4 rounded-xl" style={{ border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                    <p className="text-sm font-medium mb-1" style={{ color: textPrimary }}>Open to Internships?</p>
                    <p className="text-xs mb-3" style={{ color: textMuted }}>Summer / semester internships</p>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setComingSoonOpen(true)} className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors"
                        style={openToInternships ? { background: 'rgba(5,150,105,0.2)', borderColor: 'rgba(5,150,105,0.4)', color: '#059669' } : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', color: textMuted }}
                        data-testid="internship-yes">Yes</button>
                      <button type="button" onClick={() => setComingSoonOpen(true)} className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors"
                        style={!openToInternships ? { background: 'rgba(239,68,68,0.2)', borderColor: 'rgba(239,68,68,0.4)', color: '#dc2626' } : { background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', color: textMuted }}
                        data-testid="internship-no">No</button>
                    </div>
                  </div>
                </div>

                {comingSoonOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <div className="rounded-2xl shadow-2xl p-6 w-full text-center" style={{ maxWidth: '440px', background: isDark ? 'rgba(20,20,28,0.97)' : '#ffffff', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)', backdropFilter: 'blur(20px)' }}>
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: accentLight }}>
                        <span className="text-2xl">🚀</span>
                      </div>
                      <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Coming Soon!</h3>
                      <p className="text-sm mb-5" style={{ color: textSecondary }}>Internship matching is coming soon to WonStepCareer. We'll notify you when it launches!</p>
                      <Button type="button" onClick={() => setComingSoonOpen(false)} className="w-full font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }}>
                        Got it
                      </Button>
                    </div>
                  </div>
                )}

                {error && <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="step4-error">{error}</p>}

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => window.history.back()} className="flex-1" style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', background: 'transparent', color: textSecondary }}>Back</Button>
                  <Button type="button" onClick={handleFinish} disabled={loading} className="flex-1 font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white' }} data-testid="complete-profile-btn">
                    {loading ? "Saving..." : "Complete Profile"}
                  </Button>
                </div>
                <p className="text-center text-xs" style={{ color: textMuted }}>Your account is created when you click Complete Profile</p>
              </CardContent>
            </>
          )}
        </Card>

        {step === 1 && (
          <div className="text-center mt-4">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm" style={{ color: textMuted, textDecoration: 'none' }}>
              <ArrowLeft size={13} /> Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
